import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { RadioControl } from '../../../models/form/FormControlRadio';
import * as radioModels from '../../../../shared/controlModels/radioModels';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { AccountInfoState } from '../../../../account-info-header/models/AccountInfoState';
import { distinctUntilChanged, filter, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { selectClassifications, selectHasBundled, selectInfo, selectIsTestamentaryTrust } from '../../../../account-info-header/reducers';
import { CheckActions, PtrsActions, StandingAuthorizationsActions, StandingAuthorizationsTableActions } from '../../../actions';
import { selectCheckFrequency, selectCheckUseStandingAuth, selectIsFromEdit, selectPtrsCallFinished, selectStandingAuthId, selectCheckStandingAuthCallSucceeded, selectIsFromEditSubmittedTransaction, selectCheckInputCallSucceeded, selectIsOneTimeTotalDist, selectIsBundledOneTimeTotalDist, selectIsSloaOneTimeTotalDist } from '../../../reducers';
import { buildFromPrefilledForm, createControl } from '../../../utilities/formUtilities';
import { ChecksExtraPrompts, TransactionForm } from '../../../models/form/TransactionForm';
import { selectFilteredChecksStandingAuth, selectIsPtrs, selectLoadPopupCallFinished, selectMasterAccount, selectSubAccountId, } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { StandingAuthorizationsRequest } from '../../../models/Standing-Authorizations';
import { StandingInstruction } from 'projects/money-movement-ui/src/app/shared/types/StandingInstruction';
import { Classifications } from '../../../../account-info-header/models';
import { StandingAuthInputRequest } from '../../../models/check/CheckInputRequest';
import { selectStandingAuthTableStartTransactionClickedCheck } from 'projects/money-movement-ui/src/app/shared/reducers';
import { ModifiedFirstPartiesActions } from 'projects/money-movement-ui/src/app/modified-first-parties/actions';
import { CardControl } from 'projects/money-movement-ui/src/app/shared/models/CardControl';
import { StandingAuthorizationCard } from 'projects/money-movement-ui/src/app/shared/components/standing-authorization-card-container/standing-authorization-card-container.component';
import { BundledStandingAuthValues, CheckExtraPromptsForm, ExtraPromptsForm, FrequencyValues, TransactionFormGroup, UseStandingAuthValues } from '../../../models/form/TransactionFormGroup';
import { Constants, totalDistributionConstants } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectEnableUseSloaContainer, selectIsEnableRecurringChecksStandingAuth } from 'projects/money-movement-ui/src/app/feature-flags/reducers';

@Component({
  standalone: false,
  selector: 'mm-check-extra-prompts',
  templateUrl: './check-extra-prompts.component.html',
  styleUrls: ['./check-extra-prompts.component.scss']
})
export class CheckExtraPromptsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup<TransactionFormGroup>;
  @Input() prefilledPrompts!: ChecksExtraPrompts | undefined;
  @Input() prefilledPromptsObs: Observable<ChecksExtraPrompts>;
  @Output() extraPromptsComplete = new EventEmitter<boolean>();
  @Output() extraPromptsUpdatedInEdit = new EventEmitter();
  @Input() editFormDetails: TransactionForm | undefined;
  private readonly standingAuthControlName: string = 'standingAuthId';
  destroy$ = new Subject<void>();
  showFrequencyControl: boolean;
  showStandingAuthControl: boolean;
  isFromEdit: boolean;
  accountInfo: AccountInfoState;
  classifications: Classifications;
  totalDistributionTooltip: string = Constants.totalDistributionTooltip;
  frequencySelectorModel: RadioControl = {
    title: 'Frequency',
    controlName: 'frequency',
    radios: [
      {
        value: 'onetime',
        label: ['One-time'],
      },
    ],
    validationMessage: 'Select a frequency'
  };
  isPtrs: boolean;
  masterAccountId: string;
  showFrequencySelection: boolean;
  showStandingAuthSelection: boolean = false;
  showBundledStandingAuthSelection: boolean = false;
  enableUseSloaContainer: boolean = false;
  bundledCardModel: CardControl = {
    title: 'Select a bundled standing authorization profile',
    controlName: this.standingAuthControlName,
    radios: [],
    validationMessage: 'Select a bundled authorization',
  };
  standingAuthCardModel: CardControl = {
    title: 'Select a standing authorization',
    controlName: this.standingAuthControlName,
    radios: [],
    validationMessage: 'Select a standing authorization',
  };
  bsloaModel: RadioControl = {
    title: 'Use a standing authorization?',
    controlName: 'useStandingAuth',
    radios: [
      {
        value: 'yes',
        label: ['Yes, use a standing authorization'],
      },
      {
        value: 'bundled',
        label: ['Yes, use a bundled standing authorization'],
      },
      {
        value: 'no',
        label: ['No, create a one-time or recurring transaction that will require client authorization'],
      },
    ],
    validationMessage: 'Select an option'
  };
  useBundledModel: RadioControl = {
    title: 'Select one of the following',
    controlName: 'useBundledStandingAuth',
    radios: [
      {
        value: 'yes',
        label: ['Bundled standing authorization profile'],
      },
      {
        value: 'no',
        label: ['One-time (using firm\'s bundled authorization)'],
      }
    ],
    validationMessage: 'Select an option'
  };
  closeAccountModel: RadioControl = {
    title: 'Close account after total distribution?',
    controlName: 'closeAccount',
    radios: [
      {
        value: 'yes',
        label: ['Yes—you\'ll have the option to request account statements and tax forms'],
      },
      {
        value: 'no',
        label: ['No—we\'ll leave the account open']
      }

    ],
    validationMessage: 'Select yes or no'
  };
  standingInstructions: StandingInstruction[];
  standingAuthCards: StandingAuthorizationCard[] = [];
  bundledStandingAuthCards: StandingAuthorizationCard[] = [];
  subAccountId: string;
  useStandingAuthModel: RadioControl = radioModels.useStandingAuthModel;
  hasBundledOffer: boolean = false;
  useBundledPromptsSelected: boolean = false;
  useStandingAuthSelection: string;
  isOneTimeTotalDist: boolean;
  totalDistributionConstants = totalDistributionConstants;
  isTestamentaryTrust: boolean;
  isBundledOneTimeTotalDist: boolean;
  isSloaOneTimeTotalDist: boolean;
  y4StandingAuthErrorMessage: string = Constants.y4StandingAuthErrorMessage;
  enableRecurringChecksStandingAuth: boolean = false;

  private fb = inject(FormBuilder);
  private store = inject(Store);

  ngOnInit(): void {
    const checkFormGroup: FormGroup<CheckExtraPromptsForm> = this.fb.nonNullable.group<CheckExtraPromptsForm>({
      useStandingAuth: this.fb.nonNullable.control('', Validators.required),
    });

    this.store.select(selectClassifications).pipe(takeUntil(this.destroy$)).subscribe(classifications => { this.classifications = classifications; });
    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(isPtrs => { this.isPtrs = isPtrs; });
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => { this.subAccountId = subAccountId; });
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$)).subscribe(masterAccountId => { this.masterAccountId = masterAccountId; });
    this.store.select(selectHasBundled).pipe(takeUntil(this.destroy$)).subscribe(hasBundled => this.hasBundledOffer = hasBundled);
    this.store.select(selectIsEnableRecurringChecksStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(enableRecurringChecksStandingAuth => {
      this.enableRecurringChecksStandingAuth = enableRecurringChecksStandingAuth;
    });

    this.store.select(selectEnableUseSloaContainer).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.enableUseSloaContainer = val;
    });

    const standingAuthSelection$ = this.store.select(selectCheckUseStandingAuth).pipe(
      distinctUntilChanged(),
      filter((val: 'yes' | 'no' | 'bundled' | 'bundled-yes' | 'bundled-no' | 'bundled-recurring' | 'bundled-totalDistribution' | '') => val !== '')
    );

    this.parentForm.setControl('extraPrompts', checkFormGroup);

    this.store.select(selectFilteredChecksStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(instructions => {
      this.standingInstructions = instructions;

      this.bundledCardModel.radios = instructions?.map((auth) => {
        const label = [
          { text: 'To: ' + auth.to1 },
          { text: auth.nickname },
          { text: auth?.address ?? '' },
          { text: auth.isThirdParty ? '3rd party' : '1st party' },
        ];

        return {
          value: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
          label
        };
      }) ?? [];

      this.standingAuthCardModel.radios = instructions?.map((auth) => {
        let label = [
          { text: auth.to1 },
          { text: auth.address },
          { label: 'Profile type: ', text: (auth.isThirdParty ? '3rd party' : '1st party') },
        ];
        if (auth.nickname) {
          label.unshift({ text: auth.nickname }); //add nickname as first item in label if present
        }

        return {
          value: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
          label
        };
      }) ?? [];
      if (this.enableUseSloaContainer && instructions && instructions.length > 0) {
        this.updateStandingAuthorizationCards(instructions);
      }
    });

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.accountInfo = info;
    });

    standingAuthSelection$.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
        this.store.select(selectStandingAuthTableStartTransactionClickedCheck)
      ),
      filter(([_standingAuthSelection, isFromEdit, startTransactionClicked]) => !isFromEdit && !startTransactionClicked),
      map(([standingAuthSelection, _isFromEdit, _startTransactionClicked]) => (standingAuthSelection)),
    ).subscribe((useStandingAuthSelection) => {
      this.store.dispatch(CheckActions.extraPromptsChanged());
      this.useStandingAuthSelection = useStandingAuthSelection;
      this.setFrequencyOptions();

      if (useStandingAuthSelection === 'no') {
        checkFormGroup.setControl('frequency', this.fb.nonNullable.control<typeof FrequencyValues[number]>('', Validators.required));
        if (this.standingAuthId) checkFormGroup?.removeControl('standingAuthId');
        if (this.useBundledStandingAuth) checkFormGroup.removeControl('useBundledStandingAuth');

        this.showFrequencySelection = true;
        this.useBundledPromptsSelected = false;
      }
      if (useStandingAuthSelection === 'yes') {
        checkFormGroup.setControl('standingAuthId', this.fb.nonNullable.control('', Validators.required));
        if (this.useBundledStandingAuth) checkFormGroup.removeControl('useBundledStandingAuth');
        checkFormGroup.setControl('frequency', this.fb.nonNullable.control<typeof FrequencyValues[number]>('', Validators.required));
        this.showFrequencySelection = false;
        this.useBundledPromptsSelected = false;

        this.callStandingAuthorizations(false);
      }
      if (useStandingAuthSelection === 'bundled') {
        createControl(checkFormGroup, 'useBundledStandingAuth', '', [Validators.required]);
        if (this.frequency) checkFormGroup?.removeControl('frequency');

        this.useBundledPromptsSelected = true;
        this.showFrequencySelection = false;
      }
      if (useStandingAuthSelection === 'bundled-yes') {
        checkFormGroup.setControl('standingAuthId', this.fb.nonNullable.control('', Validators.required));
        checkFormGroup.setControl('frequency', this.fb.nonNullable.control<typeof FrequencyValues[number]>('', Validators.required));

        this.showFrequencySelection = false;
        this.callStandingAuthorizations(true);
      }
      if (useStandingAuthSelection === 'bundled-no' || useStandingAuthSelection === 'bundled-totalDistribution') {
        if (this.frequency) checkFormGroup?.removeControl('frequency');
        if (this.standingAuthId) checkFormGroup?.removeControl('standingAuthId');
        this.getModifiedPayee();
        this.callChecksInput(true);
      }
      if (useStandingAuthSelection === 'bundled-recurring') {
        if (this.frequency) checkFormGroup?.removeControl('frequency');
        if (this.standingAuthId) checkFormGroup?.removeControl('standingAuthId');
        this.getModifiedPayee();
        this.callChecksInput(false);
      }
    });

    combineLatest([
      this.store.select(selectIsOneTimeTotalDist),
      this.store.select(selectIsBundledOneTimeTotalDist),
      this.store.select(selectIsSloaOneTimeTotalDist)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isOneTimeTotalDist, isBundledOneTimeTotalDist, isSloaOneTimeTotalDist]) => {
      this.isOneTimeTotalDist = isOneTimeTotalDist || isBundledOneTimeTotalDist || isSloaOneTimeTotalDist;
      this.isSloaOneTimeTotalDist = isSloaOneTimeTotalDist;
      this.isBundledOneTimeTotalDist = isBundledOneTimeTotalDist;
      if (this.isOneTimeTotalDist) {
        checkFormGroup.addControl('closeAccount', this.fb.nonNullable.control('', Validators.required));
      } else {
        checkFormGroup?.removeControl('closeAccount');
      }
    });

    this.store.select(selectCheckFrequency).pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
      withLatestFrom(this.store.select(selectIsFromEdit)),
      filter(([_frequency, isFromEdit]) => !isFromEdit),
    ).subscribe(([frequency, isFromEdit]) => {
      if (frequency !== '') {
        this.isFromEdit = isFromEdit;
        this.store.dispatch(CheckActions.extraPromptsChanged());
        this.getModifiedPayee();
        if (this.useStandingAuthSelection === 'yes' || this.useStandingAuthSelection === 'bundled-yes') {
          this.callUseSloaProfile(this.standingAuthId?.value);
        } else {
          this.callChecksInput(frequency === 'onetime' || frequency === 'onetimeTotalDist');
        }
      }
    });

    // BUILD EXTRAPROMPTS WITH PREFILLED PROMPT WHEN PREFILLEDPROMPTSOBS EMITS OR WHEN START TRANSACTION CHECK IS SELECTED
    combineLatest([
      this.prefilledPromptsObs,
      this.store.select(selectStandingAuthTableStartTransactionClickedCheck),
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([extraPrompts, startTransactionClicked]) => !!extraPrompts && startTransactionClicked),
      map(([extraprompts, _startTransactionClicked]) => extraprompts),
      distinctUntilChanged()
    ).subscribe(extraPrompts => {
      if (extraPrompts) {
        buildFromPrefilledForm(extraPrompts, this.extraPromptsGroup);
        this.useBundledPromptsSelected = extraPrompts.useStandingAuth === 'bundled';
        this.callStandingAuthorizations();
      }
    });

    // Once standing auth call succeeds, call input and mark start transaction flow as complete
    this.store.select(selectCheckStandingAuthCallSucceeded).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedCheck), this.prefilledPromptsObs),
      filter(([callSucceeded, startTransactionClicked, _extraPrompts]) => callSucceeded && startTransactionClicked),
      map(([_callSucceeded, _startTransactionClicked, extraPrompts]) => extraPrompts)
    ).subscribe((extraPrompts) => {
      this.useStandingAuthSelection = extraPrompts.useStandingAuth;
      if (this.useStandingAuthSelection === 'bundled' && extraPrompts.useBundledStandingAuth === 'yes') {
        this.useStandingAuthSelection = 'bundled-yes';
      }
      this.setFrequencyOptions();
      if (this.useStandingAuthSelection === 'yes' || this.useStandingAuthSelection === 'bundled-yes') {
        if (!this.extraPromptsGroup.contains('frequency')) {
          (this.extraPromptsGroup as FormGroup).addControl('frequency', this.fb.nonNullable.control('', Validators.required));
        }
      }
      this.callUseSloaProfile(extraPrompts.standingAuthId ?? '');
      this.store.dispatch(StandingAuthorizationsTableActions.startTransacionFlowComplete());
    });

    //STANDING AUTH INPUT CALL
    this.store.select(selectStandingAuthId).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
        this.store.select(selectStandingAuthTableStartTransactionClickedCheck)
      ),
      filter(([_standingAuthId, isFromEdit, startTransactionClicked]) => !isFromEdit && !startTransactionClicked),
      map(([standingAuthId, _isFromEdit, _startTransactionClicked]) => standingAuthId),
      filter(standingAuthId => standingAuthId !== '')
    ).subscribe(() =>{
      this.store.dispatch(CheckActions.extraPromptsChanged());

      if ((this.useStandingAuthSelection === 'yes' || this.useStandingAuthSelection === 'bundled-yes') && this.frequency?.value !== '') {
        this.frequency.reset();
      }
      this.showFrequencySelection = true;
    });

    this.store.select(selectLoadPopupCallFinished).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(standingAuthSelection$),
      filter(([finished, _]) => finished)
    ).subscribe(([_, selection]) => {
      checkFormGroup.addControl('standingAuthId', this.fb.nonNullable.control('', Validators.required));
      if (selection === 'yes') {
        this.showStandingAuthSelection = true;
        this.showBundledStandingAuthSelection = false;
      }
      if (selection === 'bundled-yes') {
        this.showBundledStandingAuthSelection = true;
        this.showStandingAuthSelection = false;
      }
    });

    // Normal straight-through flow - when the user selects yes for use standing auth and the call to standing authorizations finished or when edit flow, show the next control
    standingAuthSelection$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectCheckStandingAuthCallSucceeded),
        this.store.select(selectStandingAuthTableStartTransactionClickedCheck),
        this.store.select(selectIsFromEdit),
        this.store.select(selectIsFromEditSubmittedTransaction)
      ),
      filter(([_standingAuthSelection, _standingAuthCallFinished, startTransactionClicked, _isFromEdit, _isFromEditSubmittedTransaction]) => !startTransactionClicked)
    ).subscribe(([standingAuthSelection, standingAuthCallFinished, _startTransactionClicked, isFromEdit, isFromEditSubmittedTransaction]) => {
      if (standingAuthSelection === 'yes' && (standingAuthCallFinished || isFromEdit) && !isFromEditSubmittedTransaction) {
        this.showStandingAuthSelection = true;
        this.showBundledStandingAuthSelection = false;
      }
      if (standingAuthSelection === 'bundled-yes' && (standingAuthCallFinished || isFromEdit) && !isFromEditSubmittedTransaction) {
        this.showBundledStandingAuthSelection = true;
        this.showStandingAuthSelection = false;
        this.useBundledPromptsSelected = true;
      }
      if (standingAuthSelection === 'bundled' || standingAuthSelection === 'bundled-no' || standingAuthSelection === 'bundled-recurring' || standingAuthSelection === 'bundled-totalDistribution') {
        this.showBundledStandingAuthSelection = false;
        this.showStandingAuthSelection = false;
        this.useBundledPromptsSelected = true;
      }
      if (standingAuthSelection === 'no') {
        this.showBundledStandingAuthSelection = false;
        this.showStandingAuthSelection = false;
        this.useBundledPromptsSelected = false;
      }
    });

    standingAuthSelection$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((standingAuthSelection) => {
      this.useStandingAuthSelection = standingAuthSelection;
      if (standingAuthSelection === 'no' || ((standingAuthSelection === 'yes' || standingAuthSelection === 'bundled-yes') && this.isFromEdit)) {
        this.showFrequencySelection = true;
      } else {
        this.showFrequencySelection = false;
      }
    });

    // ExtraPromptsComplete needs to come before build from prefilled form
    combineLatest([
      this.extraPromptsGroup.statusChanges,
      this.store.select(selectCheckInputCallSucceeded),
      this.store.select(selectPtrsCallFinished)
    ]).pipe(
      takeUntil(this.destroy$),
      map(([_status, inputCallSucceeded, ptrsCallFinished]) => {
        // Don't emit true if Y4 standing auth error exists
        return this.isPtrs ? this.extraPromptsCloseAccountValid && inputCallSucceeded && ptrsCallFinished : this.extraPromptsCloseAccountValid && inputCallSucceeded;
      }),
      distinctUntilChanged()
    ).subscribe(
      val => this.extraPromptsComplete.emit(val)
    );

    // EDIT
    this.store.select(selectIsFromEdit).pipe(
      takeUntil(this.destroy$),
      tap((isfromEdit) => (this.isFromEdit = isfromEdit)),
      filter((isFromEdit) => isFromEdit)
    ).subscribe(() => {
      buildFromPrefilledForm(
        this.prefilledPrompts,
        this.extraPromptsGroup,
        undefined,
        this.extraPromptsUpdatedInEdit
      );
    });

    this.store.select(selectIsTestamentaryTrust).pipe(takeUntil(this.destroy$)).subscribe(isTestTrust => {
      this.isTestamentaryTrust = isTestTrust;
    });

    this.store.select(selectStandingAuthId).pipe(takeUntil(this.destroy$)).subscribe(standingAuthId => {
      if ((checkFormGroup.get('useStandingAuth')?.value === 'yes' || checkFormGroup.get('useBundledStandingAuth')?.value === 'bundled-yes') && checkFormGroup.contains('frequency')) {
        if (checkFormGroup.get('frequency')?.value !== '' && !this.isFromEdit) {
          checkFormGroup.get('frequency')?.setValue('');
        }
      }
      if (this.isFromEdit) {
        this.setFrequencyOptions();
        if (this.enableUseSloaContainer && this.standingInstructions && this.standingInstructions.length > 0) {
          this.updateStandingAuthorizationCards(this.standingInstructions);
        }
      }
    });
  }

  private updateStandingAuthorizationCards(instructions: StandingInstruction[] | undefined): void {
    this.bundledStandingAuthCards = instructions?.map((auth) => ({
      id: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
      title: auth.nickname || auth.to1 || '',
      subtitle: auth.to1 ? `To: ${auth.to1}` : '',
      details: [auth.address ?? '', auth.isThirdParty ? '3rd party' : '1st party'].filter(Boolean),
      selected: this.standingAuthId?.value === `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`
    })) ?? [];

    this.standingAuthCards = instructions?.map((auth) => ({
      id: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
      title: auth.nickname || auth.to1 || '',
      subtitle: auth.nickname ? auth.to1 : '',
      details: [auth.address ?? '', `Profile type: ${auth.isThirdParty ? '3rd party' : '1st party'}`].filter(Boolean),
      selected: this.standingAuthId?.value === `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`
    })) ?? [];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  get useStandingAuth(): FormControl<typeof UseStandingAuthValues[number]> {
    return this.extraPromptsGroup?.get('useStandingAuth') as FormControl<typeof UseStandingAuthValues[number]>;
  }

  get frequency(): FormControl<typeof FrequencyValues[number]> {
    return this.extraPromptsGroup?.get('frequency') as FormControl<typeof FrequencyValues[number]>;
  }

  get extraPromptsGroup(): FormGroup<ExtraPromptsForm> {
    return this.parentForm?.get('extraPrompts') as FormGroup<ExtraPromptsForm>;
  }

  // Checks total distribution close account fields are valid
  get extraPromptsCloseAccountValid(): boolean {
    if (this.extraPromptsGroup.status === 'VALID') {
      return true;
    }

    const excludedControls = [
      'taxFormsDateInputControl',
      'accountStatementsDateInputControl',
      'tradeConfirmationsDateInputControl'
    ];

    // Check if any non-excluded control is invalid
    return !Object.keys(this.extraPromptsGroup.controls).some(controlName => {
      const control = this.extraPromptsGroup.get(controlName);
      return control?.invalid && !excludedControls.includes(controlName);
    });
  }

  get standingAuthId(): FormControl<string> {
    return this.extraPromptsGroup?.get('standingAuthId') as FormControl<string>;
  }

  public setFrequencyOptions(): void {
    this.frequencySelectorModel.radios = [
      { value: 'onetime', label: ['One-time'] }
    ];
    this.useBundledModel.radios = [
      { value: 'yes', label: ['Bundled standing authorization profile'] },
      { value: 'no', label: ['One-time (using firm\'s bundled authorization)'] }
    ];

    if (this.useStandingAuthSelection === 'yes') {
      this.frequencySelectorModel.radios.splice(this.frequencySelectorModel.radios?.length, 0, { value: 'onetimeTotalDist', label: ['One-time total distribution'] });
      if (this.enableRecurringChecksStandingAuth && !this.isPtrs) {
        this.frequencySelectorModel.radios.splice(this.frequencySelectorModel.radios?.length, 0, { value: 'recurring', label: ['Recurring'] });
      }
    } else if (this.useStandingAuthSelection === 'bundled-yes') {
      this.frequencySelectorModel.radios.splice(1, 0, { value: 'onetimeTotalDist', label: ['One-time total distribution'] });
      this.useBundledModel.radios.splice(2, 0, { value: 'onetimeTotalDist', label: ['One-time total distribution (using firm\'s bundled authorization)'] });
      if (!this.isPtrs) {
        this.useBundledModel.radios.splice(3, 0, { value: 'recurring', label: ['Recurring (using firm\'s bundled authorization)'] });
      }
      if (this.enableRecurringChecksStandingAuth && !this.isPtrs) {
        this.frequencySelectorModel.radios.splice(this.frequencySelectorModel.radios?.length, 0, { value: 'recurring', label: ['Recurring'] });
      }
    } else {
      this.frequencySelectorModel.radios.splice(1, 0, { value: 'onetimeTotalDist', label: ['One-time total distribution'] });
      this.useBundledModel.radios.splice(2, 0, { value: 'onetimeTotalDist', label: ['One-time total distribution (using firm\'s bundled authorization)'] });
      if (!this.isPtrs) {
        this.frequencySelectorModel.radios.splice(2, 0, { value: 'recurring', label: ['Recurring'] });
        this.useBundledModel.radios.splice(3, 0, { value: 'recurring', label: ['Recurring (using firm\'s bundled authorization)'] });
      }
    }
  }

  get showBundledPrompts(): boolean {
    return this.hasBundledOffer;
  }

  onStandingAuthCardSelected(cardId: string): void {
    this.standingAuthId?.setValue(cardId);
    this.standingAuthId?.markAsTouched();
    this.standingAuthCards = this.standingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
    this.bundledStandingAuthCards = this.bundledStandingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
  }

  get useBundledStandingAuth(): FormControl<typeof BundledStandingAuthValues[number]> {
    return this.extraPromptsGroup?.get('useBundledStandingAuth') as FormControl<typeof BundledStandingAuthValues[number]>;
  }

  callStandingAuthorizations(bundled: boolean = false): void {
    const standingAuthRequest: StandingAuthorizationsRequest = {
      subAccountId: this.accountInfo?.accountId,
      masterAccountId: this.accountInfo?.masterAccount,
      sloaType: bundled ? 'bundledcheck' : 'C'
    };
    this.store.dispatch(StandingAuthorizationsActions.loadSloas(standingAuthRequest));
  }

  getModifiedPayee(): void {
    this.store.dispatch(ModifiedFirstPartiesActions.getModifiedFirstParties({ subaccount: this.subAccountId, transactionType: 'checks' }));
  }

  callChecksInput(isOneTime: boolean) {
    if (isOneTime) {
      this.store.dispatch(CheckActions.callOneTimeInput());
    } else {
      this.store.dispatch(CheckActions.callRecurringInput());
    }
    if (this.isPtrs && !this.isFromEdit) {
      this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
    }
  }

  callUseSloaProfile(standingAuthId: string): void {
    const instruction: StandingInstruction = this.standingInstructions.find(instruction => instruction.cashieringRqstId === standingAuthId.split('-')[1])!;
    if (this.standingAuthId.valid) {
      const inputRequest: StandingAuthInputRequest = {
        masterAccountId: this.accountInfo?.masterAccount,
        subAccountId: this.accountInfo?.accountId,
        cashieringRequestId: parseInt(instruction.cashieringRqstId),
        cashieringRqstSecondId: parseInt(instruction.cashieringRqstSecondId),
        zaction: 'editCheck'
      };

      // Append frequency code to payload based on selection
      if (this.enableRecurringChecksStandingAuth) {
        if (this.frequency?.value === 'recurring') {
          inputRequest.frequencyCode = 'RECURRING';
        } else if (this.frequency?.value === 'onetime' || this.frequency?.value === 'onetimeTotalDist') {
          inputRequest.frequencyCode = 'ONETIME';
        }
      }

      this.store.dispatch(CheckActions.standingAuthSelected({ payload: inputRequest }));
      if (this.classifications.isPtrs) {
        this.store.dispatch(PtrsActions.callPtrs({ accountId: this.accountInfo?.accountId }));
      }
    }
    if (this.useStandingAuthSelection === 'yes' || this.useStandingAuthSelection === 'bundled-yes') {
      this.showFrequencySelection = true;
    }
  }
}
